/**
 * Serializes a comment thread to plain text for agent prompts, and decides
 * which agents (if any) should respond to a newly created comment.
 *
 * Trigger rules:
 *  - agent-authored comments NEVER trigger (loop prevention);
 *  - explicit agent @-tags win over thread ownership;
 *  - a human reply inside an agent-rooted thread re-engages that agent;
 *  - anything else stays silent.
 */
import type { CommentResponseDto } from '@inference.engine/workspace-api-client/web';
import { tiptapToPlainText } from './serialize-brief';
import { extractAgentMentions } from './agent-mentions';

export function serializeThread(root: CommentResponseDto, replies: CommentResponseDto[]): string {
  const line = (c: CommentResponseDto) =>
    `${c.author.name}: ${tiptapToPlainText(c.content as Record<string, unknown> | null)}`;
  return [line(root), ...replies.map(line)].join('\n');
}

export function resolveRespondTargets({
  newComment,
  root,
}: {
  newComment: CommentResponseDto;
  root: CommentResponseDto;
}): string[] {
  if (newComment.authorType === 'agent') return [];
  const tagged = extractAgentMentions((newComment.content ?? {}) as Record<string, unknown>);
  if (tagged.length > 0) return tagged;
  if (root.authorType === 'agent' && newComment.parentId) return [root.createdBy];
  return [];
}
